<script lang="ts">
  /**
   * Der Kopf eines Panels, in fünf Ausprägungen.
   *
   * ## Das Problem
   *
   * Zehn Reiter, zehn Panels, ein Aussehen. Jedes beginnt mit derselben
   * Kennzahlenzeile, jedes setzt danach Überschrift, Liste, Knöpfe in denselben
   * Abständen. Man erkennt am Bild nicht, wo man ist — nur am unterstrichenen
   * Reiter.
   *
   * ## Die Antwort: fünf Arten statt zehn gleicher Flächen
   *
   * Die Panels tun sehr Verschiedenes, und die Unterschiede sind benennbar.
   * Jede Art bekommt einen eigenen Kopf mit eigener Rangordnung:
   *
   * | `art` | Panels | Der Kopf trägt |
   * |---|---|---|
   * | `ergebnis` | Plan, Laden | **die Antwort zuerst** — ein Satz in Lesegröße, Belege darunter |
   * | `suche` | Schlafen, Entdecken, Kajak, Meine Reisen | Titel, Trefferzahl, die Suchfläche direkt darunter |
   * | `checkliste` | Vorbereitung | Titel und **Statuszahl** — „3 von 6 erledigt" |
   * | `schreiben` | Tagebuch, Tagespläne, Reiseeinstellungen | Titel und Speicherstand |
   * | `herausgeben` | Teilen, Freigaben, Druck | Titel und ein Satz, was nach außen geht |
   *
   * Die Statuszahl im Kopf ist aus dem Bestand übernommen: Der
   * Vorbereitungs-Reiter macht das schon und es ist das beste Muster darin —
   * man sieht ohne Aufklappen, ob etwas zu tun ist.
   *
   * Fünf Arten sind **weniger** Arbeit als zehn identische Flächen, nicht mehr:
   * Jedes Panel wählt eine und hat damit seine Rangordnung.
   *
   * ## Anders als in der Vorlage
   *
   * Die React-Fassung ist eine Kette aus fünf frühen `return`s, in der jeder
   * Zweig sein eigenes Markup mitbringt. Hier steht **ein** Kopf, und was er
   * zeigt, sagt `kopfbau(art)` aus `muster.ts`. Grund: So lässt sich die
   * Zuordnung ohne Browser prüfen — und zwei Panels derselben Art können sich
   * nicht mehr auseinanderentwickeln, weil sie durch dieselbe Vorlage laufen.
   */
  import type { Snippet } from 'svelte';
  import { kopfbau, erledigtAnteil, erledigtWort, type Panelart } from './muster';

  interface Props {
    art?: Panelart;
    titel: string;
    /** Nur bei `ergebnis`: der eine Satz, der die Frage beantwortet. */
    antwort?: string | null;
    /** Nur bei `ergebnis`: die Kennzahlen, die den Satz belegen. */
    belege?: Snippet;
    /** Nur bei `schreiben`: der Speicherstand. */
    stand?: Snippet;
    /** `checkliste`: Zahl der offenen Punkte. `suche`: Trefferzahl. */
    offen?: number | null;
    /** Nur bei `checkliste`: Gesamtzahl der Punkte. */
    gesamt?: number | null;
    hinweis?: string | null;
    /** Die eine Handlung rechts im Kopf. */
    handlung?: Snippet;
    /** Bei `suche`: die Suchfläche. */
    children?: Snippet;
  }
  let {
    art = 'suche',
    titel,
    antwort = null,
    belege,
    stand,
    offen = null,
    gesamt = null,
    hinweis = null,
    handlung,
    children
  }: Props = $props();

  const bau = $derived(kopfbau(art));
  const anteil = $derived(erledigtAnteil(offen, gesamt));
</script>

<header class="kopf {art}" class:linie={bau.trennlinie}>
  {#if bau.ueberschrift === 'etikett'}
    <!-- Bei `ergebnis` ist der Antwortsatz die Überschrift. Ein zweiter fetter
         Titel darüber würde mit ihm konkurrieren, deshalb steht der Panelname
         hier nur als Etikett. -->
    <span class="etikett">{titel}</span>
  {:else}
    <div class="zeile">
      <h2>
        {titel}{#if bau.trefferzahl && offen != null}<span class="treffer" data-zahl>{offen}</span>{/if}
      </h2>
      {#if bau.stand && stand}{@render stand()}{/if}
      {#if gesamt != null && bau.balken}
        <span class="wort" class:offen={Boolean(offen)}>{erledigtWort(offen)}</span>
      {/if}
      {#if handlung}{@render handlung()}{/if}
    </div>
  {/if}

  {#if bau.antwort && antwort}
    <p class="antwort">{antwort}</p>
  {/if}

  {#if bau.belege && belege}
    <div class="belege" data-zahl>{@render belege()}</div>
  {/if}

  {#if bau.balken && gesamt}
    <!-- Ein Balken zum Bruch, weil man den Anteil schneller sieht als „2 von 6". -->
    <span class="bahn" aria-hidden="true">
      <span class="fuellung" style:width="{anteil * 100}%"></span>
    </span>
  {/if}

  <!-- Bei `suche` steht die Suchfläche über dem erklärenden Satz: Wer sucht,
       will das Feld, nicht die Begründung. Bei allen anderen Arten gibt es
       keine Kinder, dann ist die Reihenfolge folgenlos. -->
  {#if children}{@render children()}{/if}

  {#if hinweis}
    <p class="hinweis">{hinweis}</p>
  {/if}
</header>

<style>
  /* `--kopf-fuss` und kein fester Wert: Der Panelkopf steht ueber jeder Liste
     der Anwendung, und wer den Dichteschalter umlegt, sieht ihn elfmal. Er
     stand vorher fest auf `--gruppe-eng` und aenderte sich nie. Die einfache
     Fassung behaelt genau dieses Mass — der Schalter macht die Expertenansicht
     enger, er macht die Vorgabe nicht anders. */
  .kopf {
    display: grid;
    gap: var(--raum-4);
    padding-bottom: var(--kopf-fuss);
  }
  .kopf.ergebnis,
  .kopf.schreiben,
  .kopf.herausgeben {
    gap: var(--raum-3);
  }

  /* Nur beim Herausgeben: Was nach außen geht, bekommt eine Kante. Diese
     Panels haben Folgen, die man nicht zurücknehmen kann — ein geteilter Link
     ist geteilt. */
  .linie {
    border-bottom: 1px solid var(--border);
  }

  .etikett {
    font-family: var(--schrift);
    font-size: var(--fs-mikro);
    text-transform: uppercase;
    letter-spacing: var(--lw-etikett);
    color: var(--text-schwach);
    font-weight: var(--gw-halb);
  }

  .zeile {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--raum-5);
    flex-wrap: wrap;
  }

  h2 {
    margin: 0;
    font-size: var(--fs-panel);
    font-weight: var(--gw-halb);
  }

  /* Die Trefferzahl steht neben dem Titel und nicht über der Liste — sonst
     liest man sie erst, wenn man die Liste schon sieht. */
  .treffer {
    margin-left: var(--raum-4);
    font-weight: var(--gw-normal);
    color: var(--text-dim);
  }

  .wort {
    font-size: var(--fs-klein);
    color: var(--zustand-frei);
    font-variant-numeric: var(--zahl-variante);
  }
  .wort.offen {
    color: var(--merk);
  }

  .antwort {
    margin: 0;
    font-size: var(--fs-titel);
    line-height: var(--zh-eng);
    font-weight: var(--gw-halb);
    text-wrap: pretty;
  }

  /* Nach Inhalt umbrechend und nicht in feste Drittel: Bei einer
     Verbrenner-Reise entfällt die Kennzahl „am Ziel", und die verbleibenden
     rücken auf, statt eine leere Spalte stehen zu lassen. */
  .belege {
    display: flex;
    gap: var(--raum-8);
    flex-wrap: wrap;
    font-variant-numeric: var(--zahl-variante);
  }

  .bahn {
    height: 4px;
    border-radius: var(--radius-rund);
    background: var(--soc-bahn);
    overflow: hidden;
  }
  .fuellung {
    display: block;
    height: 100%;
    background: var(--zustand-frei);
  }

  .hinweis {
    margin: 0;
    font-size: var(--fs-fein);
    color: var(--text-dim);
    max-width: 52ch;
  }
</style>
